// 面板來電顯示的名字（John 2026-10-22，docs/panel/08-call-family）：
//   - PanelCallNameCard（面板頁）：主人設「打電話時顯示的名字」（面板設定 caller_name，"" = 用面板名稱）；
//     每個人（含主人）設「我怎麼稱呼這台」（PUT /v3/panels/{id}/my-name，只影響自己的手機）
//   - MyPanelNamesCard（帳號頁，面板 PIN 旁）：GET /v3/me/panels 每台一列改「我怎麼稱呼這台」（看不到面板頁的家人也能改；列表空就不顯示）
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CALLER_NAME_MAX, callerNameOf, getPanelMyName, getPanelSettings, listMyPanels, putPanelCallerName, putPanelMyName, type MyPanelEntry,
} from './panels';

const tooLong = (s: string) => Array.from(s.trim()).length > CALLER_NAME_MAX;

/** 一行文字欄位 + ［儲存］（placeholder = 空白時會用的名字；存好的提示：叫法只有自己看得到，不是「面板會立刻套用」） */
function NameField({ id, label, hint, initial, placeholder, savedKey, onSave }: {
  id: string; label: string; hint: string; initial: string; placeholder: string; savedKey: string; onSave: (v: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [v, setV] = useState(initial);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => setV(initial), [initial]);
  const save = async () => {
    if (tooLong(v)) { setMsg(t('panels.myName.tooLong')); setErr(true); return; }
    setBusy(true);
    setMsg(null);
    try {
      await onSave(v.trim());
      setMsg(t(savedKey));
      setErr(false);
    } catch {
      setMsg(t('panels.errGeneric'));
      setErr(true);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid={id}>
      <label className="dv-pairing-input">
        <span>{label}</span>
        <input value={v} placeholder={placeholder} maxLength={CALLER_NAME_MAX + 10} onChange={(e) => setV(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') void save(); }} />
      </label>
      {hint && <p className="dv-detail-sub">{hint}</p>}
      <div className="dv-pairing-actions">
        <button className="btn-primary" disabled={busy || v.trim() === initial.trim()} onClick={() => void save()}>{t('common.save')}</button>
      </div>
      {msg && <p className={err ? 'error-text' : 'text-secondary'}>{msg}</p>}
    </div>
  );
}

export function PanelCallNameCard({ panelId, panelName, isOwner }: { panelId: string; panelName: string; isOwner: boolean }) {
  const { t } = useTranslation();
  const [caller, setCaller] = useState<string | null>(isOwner ? null : '');
  const [mine, setMine] = useState<string | null>(null);
  const load = useCallback(() => {
    if (isOwner) getPanelSettings(panelId).then((s) => setCaller(callerNameOf(s))).catch(() => setCaller(''));
    getPanelMyName(panelId).then((r) => { setMine(r.my_name); if (!isOwner) setCaller(r.caller_name); }).catch(() => setMine(''));
  }, [panelId, isOwner]);
  useEffect(load, [load]);
  if (caller === null || mine === null) return null;
  return (
    <div className="card" data-testid="panel-call-names">
      {isOwner && (
        <NameField id="panel-caller-name" label={t('panels.callName.title')} hint={t('panels.callName.hint')} initial={caller}
          placeholder={panelName} savedKey="panels.callName.saved" onSave={async (v) => { await putPanelCallerName(panelId, v); setCaller(v); }} />
      )}
      <NameField id="panel-my-name" label={t('panels.myName.title')} hint={t('panels.myName.hint')} initial={mine}
        placeholder={caller || panelName} savedKey="panels.myName.saved" onSave={async (v) => { await putPanelMyName(panelId, v); setMine(v); }} />
    </div>
  );
}

export function MyPanelNamesCard() {
  const { t } = useTranslation();
  const [list, setList] = useState<MyPanelEntry[]>([]);
  useEffect(() => {
    listMyPanels().then(setList).catch(() => setList([]));
  }, []);
  if (!list.length) return null;
  return (
    <div className="card" data-testid="my-panel-names">
      <h2>{t('panels.myName.listTitle')}</h2>
      <p className="dv-detail-sub">{t('panels.myName.hint')}</p>
      {list.map((p) => (
        <NameField key={p.panel_device_id} id={`my-panel-name-${p.panel_device_id}`} label={p.panel_name || p.panel_device_id} hint=""
          initial={p.my_name} placeholder={p.caller_name || p.panel_name} savedKey="panels.myName.saved"
          onSave={async (v) => { await putPanelMyName(p.panel_device_id, v); setList((l) => l.map((x) => (x.panel_device_id === p.panel_device_id ? { ...x, my_name: v } : x))); }} />
      ))}
    </div>
  );
}
